import { Modal } from 'react-daisyui';

/**
 * The frame a Lead Agent checkout shows while its OWN copy is still in flight.
 *
 * WHY THIS EXISTS. Translations are not bundled: `src/i18n.ts` fetches one JSON
 * per namespace over HTTP, and it is configured `useSuspense: false` — so a
 * component renders IMMEDIATELY whether or not its namespace has arrived, and
 * `t('confirm.title')` hands back the KEY until it does. Normally that is a
 * flash; on a slow connection, a failed request or a half-rolled deploy it is
 * the final state.
 *
 * These four modals are the worst place for it. Their namespace is not fetched
 * at boot — it is fetched when the modal OPENS, which is the moment the owner
 * clicked to pay. And the amounts do NOT go through i18n, so the failure mode
 * is a screen with correct money and broken labels: it reads as a broken app,
 * not as something loading. (Fanny hit exactly this, 2026-09-02.)
 *
 * `ConfirmTierModal` already guarded the BILLING namespace this way (RET-1372,
 * "fall back rather than surfacing a raw i18n key") — but not its own. This is
 * that same guard, extended to the namespace each modal is written in.
 *
 * NO COPY IN HERE, deliberately: every loading string these modals own lives in
 * the very namespace we are waiting for, so `t('...loading')` would render a
 * raw key too. A spinner says "wait" in every language.
 *
 * The frame stays so the click still produces a modal — a button that appears
 * to do nothing is worse than one that opens and spins — and the backdrop stays
 * closeable so nobody is trapped if the fetch never lands.
 */
export type tCheckoutCopyGateProps = {
  /** The host modal's own class, so the gate matches its footprint. */
  className: string;
  onClose: () => void;
};

export default function CheckoutCopyGate(props: tCheckoutCopyGateProps) {
  return (
    <Modal.Legacy
      open
      onClickBackdrop={props.onClose}
      className={props.className}
    >
      {/* El testid va AQUÍ, no en `Modal.Legacy`: react-daisyui no reenvía
          props arbitrarias, que es también la razón de que estos modales no
          tengan un testid en su raíz. */}
      <div
        className="flex items-center justify-center p-10"
        aria-busy="true"
        data-testid="checkout-copy-gate"
      >
        <span
          className="loading loading-spinner loading-lg text-primary"
          role="status"
          /*
           * The ONE string here, and untranslated on purpose: it cannot come
           * from `t()` when the missing translations are what we are waiting
           * for, and an empty `role="status"` announces nothing to a screen
           * reader. Spanish because it is both the `fallbackLng` and the
           * language production is locked to (see `src/i18n.ts`).
           */
          aria-label="Cargando"
        />
      </div>
    </Modal.Legacy>
  );
}
